import React, {createContext, useContext} from 'react';
import Axios from 'axios';

import {
  action,
  observable,
  makeObservable,
  reaction,
  runInAction,
} from 'mobx';

import moment from 'moment';

const AppDataStore = function AppDataStore() {
  this.user = null;

  const axios = Axios.create({baseURL: '/api/v1'});

  this.get = async (endpoint, config = {}) => {
    try {
      if (this.user?.token) {
        if (!config.headers) config.headers = {};
        config.headers.authorization = `Token ${this.user.token}`;
      }
      const res = await axios.get(endpoint, config);
      const {data} = res;
      return data;
    } catch (error) {
      if (error.response.status === 401) await this.logout();
      if (error.response.data) {
        const err = new Error(error.response.data.error);
        err.response = error.response;
        throw err;
      }
      throw error;
    }
  };

  this.post = async (endpoint, post = null, config = {}) => {
    try {
      if (this.user?.token) {
        if (!config.headers) config.headers = {};
        config.headers.authorization = `Token ${this.user.token}`;
      }

      const res = await axios.post(endpoint, {data: post}, config);
      const {data} = res;
      return data;
    } catch (error) {
      if (error.response.status === 401) await this.logout();
      if (error.response.data) {
        const err = new Error(error.response.data.error);
        err.response = error.response;
        throw err;
      }
      throw error;
    }
  };

  this.login = async ({email, password}) => {
    const res = await this.post('/auth/login', {
      email,
      password,
    });
    if (res.user) await this.updateUser(res.user);
    return res;
  };

  this.logout = async () => {
    try {
      await this.post('/auth/logout');
    } finally {
      runInAction(() => {
        this.user = null;
      });
    }
  };

  this.updateUser = async (user) => {
    if (!user) {
      const res = await this.post('/auth/info');
      user = res.user;
    }
    runInAction(() => {
      if (this.user === null) return (this.user = user);
      return Object.keys(user).forEach((key) => {
        if (this.user[key] !== user[key]) {
          this.user[key] = user[key];
        }
      });
    });
    return user;
  };

  makeObservable(this, {
    user: observable,
    updateUser: action,
    logout: action,
    login: action,
  });

  const loadPersistentData = () => {
    const localUser = localStorage.getItem('user');
    if (localUser && localUser !== 'undefined')
      this.user = JSON.parse(localUser);
  };

  loadPersistentData();
};

export const appDataStore = new AppDataStore();
export const AppDataContext = createContext(null);
export const useAppDataStore = () => useContext(AppDataContext);
export const AppDataProvider = ({children}) => (
  <AppDataContext.Provider value={appDataStore}>
    {children}
  </AppDataContext.Provider>
);

reaction(
  () => JSON.stringify(appDataStore.user),
  (varStr) => {
    localStorage.setItem('user', varStr);
  }
);